/** Free/taken slots for a chosen day, beside the scheduling picker.
 *
 * Reported 2026-09-24: "When scheduling an appointment, I really need to
 * be able to see the available time slots at the same time, so that I
 * can avoid overlapping appointments."
 *
 * Beneficiaries have had a Calendly-style picker since Phase D, built on
 * `list_slots_for_month`. The operator had a bare datetime field and
 * could book straight over an existing call with nothing to warn them.
 * This puts the SAME engine on the operator's side — one source of truth
 * for what "free" means, so the two views can never disagree.
 *
 * Clicking a free slot sets the time, which is the fast path: she picks
 * a day, sees what's open, and takes one.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { formatTime } from "@/lib/format";
import { localDay } from "@/lib/slotGrid";

type Slot = {
  starts_at: string;
  available: boolean;
  remaining: number;
  /** Sessions ALREADY scheduled in this slot, whoever runs them. A slot
   *  can be both available and booked: capacity counts operators, and a
   *  call booked by an org admin consumes none. */
  booked: number;
};
export type SlotsResponse = {
  date: string | null;
  operator_count: number | null;
  /** The booking grid — the time picker snaps to it. */
  slot_minutes?: number;
  offset_minutes?: number;
  slots: Slot[];
};



export function SlotAvailability({
  value,
  onPick,
  campaignId,
  excludeSessionId,
  className = "",
}: {
  /** ISO instant currently in the picker — its DAY drives the query.
   *  A string, because that is what both scheduling forms already hold. */
  value: string | null;
  onPick: (iso: string) => void;
  campaignId?: string | null;
  excludeSessionId?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const day = value ? localDay(value) : null;

  const q = useQuery({
    queryKey: ["scheduling-slots", day, campaignId ?? null, excludeSessionId ?? null],
    enabled: !!day,
    queryFn: async () => {
      const params = new URLSearchParams({ date: day as string });
      if (campaignId) params.set("campaign", campaignId);
      if (excludeSessionId) params.set("exclude_session", excludeSessionId);
      const res = await api.get<SlotsResponse>(
        `${ApiPaths.schedulingAvailability}?${params.toString()}`,
      );
      return res.data;
    },
  });

  if (!day) return null;

  const slots = q.data?.slots ?? [];
  const free = slots.filter((s) => s.available).length;
  const bookedTotal = slots.reduce((n, s) => n + (s.booked ?? 0), 0);
  const picked = value ? new Date(value).getTime() : null;

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-nb-text-2">
          {t("scheduling.slotsForDay")}
        </span>
        {q.data && (
          <span className="text-[11px] text-nb-text-3">
            {t("scheduling.slotsFree", { count: free })}
            {bookedTotal > 0 && (
              <span className="ml-2 text-amber-400">
                {t("scheduling.slotsBooked", { count: bookedTotal })}
              </span>
            )}
          </span>
        )}
      </div>

      {q.isLoading && (
        <p className="text-xs text-nb-text-3">{t("scheduling.slotsLoading")}</p>
      )}
      {q.isError && (
        <p className="text-xs text-cvp-danger">{t("scheduling.slotsError")}</p>
      )}
      {q.data && slots.length === 0 && (
        <p className="text-xs text-nb-text-3">{t("scheduling.slotsClosed")}</p>
      )}
      {q.data && slots.length > 0 && free === 0 && (
        <p className="text-xs text-amber-400">{t("scheduling.slotsNoneFree")}</p>
      )}

      {slots.length > 0 && (
        <>
          {/* Tiles rather than a run of inline chips. Reported
              2026-09-27: the strip was hard to read — 11px text, chips
              of differing width wrapping mid-row, and the only sign that
              a free slot already held an appointment was a "●2" the size
              of a full stop.

              A fixed grid gives every slot the same footprint, so the
              eye scans down columns of times instead of re-finding the
              left edge on each row, and there is room to SAY "2 booked"
              rather than encode it. */}
          <div
            className="grid max-h-60 grid-cols-[repeat(auto-fill,minmax(5.25rem,1fr))] gap-1.5 overflow-y-auto pr-0.5"
            data-testid="slot-strip"
          >
            {slots.map((s) => {
              const at = new Date(s.starts_at);
              const isPicked = picked !== null && at.getTime() === picked;
              // A taken slot stays VISIBLE rather than being filtered
              // out — seeing that 10:00 is gone is the information she
              // asked for; omitting it just looks like a gap.
              const partly = s.available && s.booked > 0;
              return (
                <button
                  key={s.starts_at}
                  type="button"
                  disabled={!s.available}
                  aria-pressed={isPicked}
                  title={
                    s.available
                      ? s.booked > 0
                        ? t("scheduling.slotBookedTitle", { count: s.booked })
                        : t("scheduling.slotFreeTitle", { count: s.remaining })
                      : t("scheduling.slotTakenTitle")
                  }
                  onClick={() => onPick(at.toISOString())}
                  className={`flex flex-col items-center justify-center rounded-lg border px-1 py-1.5 transition ${
                    isPicked
                      ? "border-nb-mint bg-nb-mint/15 text-nb-mint ring-1 ring-nb-mint/40"
                      : !s.available
                        ? "cursor-not-allowed border-nb-border/40 bg-nb-bg/40 text-nb-text-3 opacity-60"
                        : partly
                          ? "border-amber-500/40 bg-amber-500/5 text-nb-text hover:border-amber-400"
                          : "border-nb-border bg-nb-surface/40 text-nb-text-2 hover:border-nb-mint hover:text-nb-text"
                  }`}
                >
                  <span
                    className={`font-mono text-sm tabular-nums ${
                      !s.available ? "line-through" : ""
                    }`}
                  >
                    {formatTime(s.starts_at)}
                  </span>
                  {/* Said in words. An available slot that already holds
                      an appointment must look different or she books on
                      top of it — the whole point of the request. */}
                  <span
                    className={`mt-0.5 text-[10px] leading-none ${
                      partly ? "text-amber-400" : "text-nb-text-3"
                    }`}
                    data-testid={partly ? "slot-booked-marker" : undefined}
                  >
                    {!s.available
                      ? t("scheduling.slotFull")
                      : partly
                        ? t("scheduling.slotBookedCount", { count: s.booked })
                        : t("scheduling.slotOpen")}
                  </span>
                </button>
              );
            })}
          </div>

          {/* The amber state used to be explained only by a title
              attribute, which a mouse never hovers in a hurry. */}
          <ul className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5 text-[10px] text-nb-text-3">
            <li className="flex items-center gap-1">
              <span
                aria-hidden
                className="h-2 w-2 rounded-sm border border-nb-border bg-nb-surface"
              />
              {t("scheduling.legendOpen")}
            </li>
            <li className="flex items-center gap-1">
              <span
                aria-hidden
                className="h-2 w-2 rounded-sm border border-amber-500/50 bg-amber-500/20"
              />
              {t("scheduling.legendPartly")}
            </li>
            <li className="flex items-center gap-1">
              <span
                aria-hidden
                className="h-2 w-2 rounded-sm border border-nb-border/40 bg-nb-bg/60"
              />
              {t("scheduling.legendFull")}
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
